import { i18n } from '@lingui/core'
import type { CleaveOptions } from 'cleave.js/options'
import dayjs from 'dayjs'
import { isNil, sum } from 'ramda'

import {
  getFormattedDate,
  isString,
  parseDateString,
  validateDateParts,
} from '@twisto/utils'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormValidatorFunction<V = any> = (value: V) => string | null
export type ValidatorFunction<V> = (value: V) => boolean

export const regexValidators = {
  lowerCase: /[a-z]/,
  upperCase: /[A-Z]/,
  specialChar: /[^a-zA-Z]/,
  email:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  cardNumber: /^(\d{8}|\d{12,19})$/,
  cardExpirationDate: /^(0[1-9]|1[0-2])\/?([0-9]{2})$/,
  cvv: /^[0-9]{3,4}$/,
}

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const gqlNumber = { min: -(2 ** 31), max: 2 ** 31 - 1 }

export const isLowerCase = (value: string) =>
  regexValidators.lowerCase.test(value)
export const isUpperCase = (value: string) =>
  regexValidators.upperCase.test(value)
export const isSpecialChar = (value: string) =>
  regexValidators.specialChar.test(value)
export const createMinLength = (length: number) => (value: string) =>
  value.length >= length
export const isEmail = (value: string) => regexValidators.email.test(value)
export const isRequired = (value: unknown): boolean =>
  value !== undefined &&
  value !== null &&
  value !== false &&
  (!isString(value) || !!value.length) &&
  Boolean(
    typeof value !== 'object' ||
      Object.values(value).every((fieldValue) => isRequired(fieldValue))
  )
export const isValidDate = (dateString: string) =>
  Object.values(validateDateParts(parseDateString(dateString))).every(
    (val) => !val
  )

export const isValidNumber = (value: number) =>
  value >= gqlNumber.min && value <= gqlNumber.max
export const isValidCardNumber = (value: string) =>
  regexValidators.cardNumber.test(value.replace(/\s+/g, ''))
export const isValidCardExpirationDate = (value: string) =>
  regexValidators.cardExpirationDate.test(value)
export const isValidCVV = (value: string) => regexValidators.cvv.test(value)

// compute length of cleave block and compare with value length
export const isValidPhoneNumber = (value: string, format: CleaveOptions) =>
  sum(format.blocks ?? []) === value.length

export const isShorterOrEqualThan = (value: string, max: number) =>
  value.length <= max

export const createStrongPassword =
  <T extends Array<ValidatorFunction<string>>>(validators: T) =>
  (value: string) =>
    validators.every((rule) => rule(value))
      ? undefined
      : /*i18n*/
        i18n._('validation.strongPassword')

export const getValidationMessages = (length?: number) => ({
  required: /*i18n*/ i18n._('validation.required'),
  email: /*i18n*/ i18n._('validation.email'),
  requiredGroup: /*i18n*/ i18n._('validation.required.group'),
  maxLength: /*i18n*/ i18n._('validation.length.max', { length }),
  validCvv: /*i18n*/ i18n._('validation.validCVV'),
  validCardNumber: /*i18n*/ i18n._('validation.validCardNumber'),
  validExpirationDate: /*i18n*/ i18n._('validation.validCardExpirationDate'),
})

type DateRange = { min?: string | null; max?: string | null }

export const isDate = (dateValue: string) => ({
  after: (afterDateValue?: string | null) => {
    const date = dayjs(dateValue)

    if (!isNil(afterDateValue)) {
      const afterDate = dayjs(afterDateValue)

      return date.isAfter(afterDate)
    }

    return false
  },
  before: (beforeDateValue?: string | null) => {
    const date = dayjs(dateValue)

    if (!isNil(beforeDateValue)) {
      const beforeDate = dayjs(beforeDateValue)

      return date.isBefore(beforeDate)
    }

    return false
  },
})

export const formValidators = {
  isRequired: (value: unknown) =>
    isRequired(value) ? undefined : getValidationMessages().required,
  hasSelectedOption: (value: unknown) =>
    isRequired(value) ? undefined : getValidationMessages().requiredGroup,
  isEmail: (value: string) =>
    isEmail(value) ? undefined : getValidationMessages().email,
  isValidDate: (value: string) =>
    isValidDate(value) ? undefined : /*i18n*/ i18n._('validation.validDate'),
  isShorterOrEqualThan: (value: string, max: number) =>
    isShorterOrEqualThan(value, max)
      ? undefined
      : getValidationMessages(max).maxLength,
  isValidDateRange: (value: string, { min, max }: DateRange) => {
    if (isDate(value).before(min)) {
      return /*i18n*/ i18n._('validation.validDate.min', {
        min: getFormattedDate(
          dayjs(min).toDate(),
          i18n.locale.split('-')[0] as GLOBALS.Language,
          {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          }
        ),
      })
    }
    if (isDate(value).after(max)) {
      return /*i18n*/ i18n._('validation.validDate.max', {
        max: getFormattedDate(
          dayjs(max).toDate(),
          i18n.locale.split('-')[0] as GLOBALS.Language,
          {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          }
        ),
      })
    }

    return undefined
  },

  isValidNumber: (value: number) =>
    isValidNumber(value)
      ? undefined
      : /*i18n*/
        i18n._('validation.validNumber'),
  isValidCardNumber: (value: string) =>
    isValidCardNumber(value)
      ? undefined
      : /*i18n*/ i18n._('validation.validCardNumber'),
  isValidCardExpirationDate: (value: string) =>
    isValidCardExpirationDate(value)
      ? undefined
      : /*i18n*/
        i18n._('validation.validCardExpirationDate'),
  isValidCvv: (value: string) =>
    isValidCVV(value) ? undefined : /*i18n*/ i18n._('validation.validCVV'),
  isValidPhoneNumber: (value: string, format: CleaveOptions) =>
    isValidPhoneNumber(value, format)
      ? undefined
      : /*i18n*/
        i18n._('validation.validPhoneNumber'),
}

export const composeFormValidators = <T extends FormValidatorFunction[]>(
  ...validatorFns: T
) =>
  function <V>(value: V): string[] | [] {
    return validatorFns.map((isValid) => isValid(value)).filter(Boolean) as
      | string[]
      | []
  }
